import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import SmallCFLoader from "../../common/SmallCFLoader/SmallCFLoader";
import EntityConstants from "../../configs/EntityConstants";
import { POST, GET } from "../../service/CRUDService";
import Toast from "../../common/Toast/Toast";
import Dropdown from "../../common/Dropdown/Dropdown";
import VerificationApprovalsLongCard from "../../components/Cards/VerificationApprovalsLongCard/VerificationApprovalsLongCard";
import { useNavigate, useLocation } from "react-router-dom";
import BulkVerificationApprovalModal from "../../common/BulkVerificationApprovalModal/BulkVerificationApprovalModal";
import CfLoader from "../../common/CfLoader/CfLoader";
import { fetchOrganisationsAPI } from "../../common/Organisations/fetchOrganisations";
import { fetchCurrentUser } from "../../service/CurrentUser";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { CalendarDays } from "lucide-react";
import { format, subDays, startOfDay, endOfDay } from "date-fns";
import { downloadFromUrl } from "../../service/DownloadFile";

const SEARCH_DEBOUNCE_DELAY = 500;
const EXPORT_POLL_INTERVAL = 3000;

const VerificationApprovals = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const APPROVAL_STATUS_MAP = {
    Approved: "Approved",
    Rejected: "Rejected",
    "Approval Pending": "Approval Pending",
  };

  const savedFilters = location.state?.filters || {};

  const limit = 6;
  const [respContentList, setRespContentList] = useState([]);
  const [previous, setPrevious] = useState(null);
  const [next, setNext] = useState(null);
  const [search, setSearch] = useState(savedFilters.search || "");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(search);
  const [isSearching, setIsSearching] = useState(false);
  const [isSearched, setIsSearched] = useState(false);
  const [offset, setOffset] = useState(savedFilters.offset || 0);
  const [selectedAmountStatus, setSelectedAmountStatus] = useState(
    savedFilters.amountStatus || null
  );
  const [selectedApprovalStatus, setSelectedApprovalStatus] = useState(
    savedFilters.approvalStatus || null
  );
  const [selectedVerifierOrganization, setSelectedVerifierOrganization] =
    useState(savedFilters.verifierOrg || null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionType, setActionType] = useState("");
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [allSelected, setAllSelected] = useState(false);
  const [issuerComments, setIssuerComments] = useState("");
  const [verifierOrganizations, setVerifierOrganizations] = useState([
    { name: "Verifier Organization", value: null },
  ]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [selectedDocumentType, setSelectedDocumentType] = useState(
    savedFilters.documentType
  );
  const [organisationOptions, setOrganisationOptions] = useState([
    { name: "All Organisations", value: "" },
  ]);
  const [orgNext, setOrgNext] = useState(null);
  const [orgLoading, setOrgLoading] = useState(false);
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const [orgOffset, setOrgOffset] = useState(0);
  const [hasOrganisations, setHasOrganisations] = useState(false);
  const orgDropdownRef = useRef(null);
  const [selectedOrganisation, setSelectedOrganisation] = useState("");
  const [org_code, setOrgCode] = useState("");
  const [userType, setUserType] = useState(null);
  const [tenantCode, setTenantCode] = useState("");
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [dateRange, setDateRange] = useState("dateselection");
  const [isExporting, setIsExporting] = useState(false);
  const isTenantRole = ["tenant_admin", "tenant_user"].includes(
    (userType ?? "").toLowerCase()
  );

  const exportIntervalRef = useRef(null);

  const formatDateForAPI = (date) => {
    if (!date) return "";
    return new Date(date).toISOString();
  };

  const getDateRangeForAPI = () => {
    const now = new Date();
    let startDate, endDate;

    switch (dateRange) {
      case "recent":
        startDate = subDays(startOfDay(now), 6);
        endDate = endOfDay(now);
        break;
      case "last30days":
        startDate = subDays(startOfDay(now), 29);
        endDate = endOfDay(now);
        break;
      case "last3months":
        startDate = subDays(startOfDay(now), 89);
        endDate = endOfDay(now);
        break;
      case "last6months":
        startDate = subDays(startOfDay(now), 179);
        endDate = endOfDay(now);
        break;
      case "all":
        return { all_transactions: true };
      case "custom":
        if (customStartDate && customEndDate) {
          startDate = startOfDay(customStartDate);
          endDate = endOfDay(customEndDate);
        } else {
          startDate = subDays(startOfDay(now), 6);
          endDate = endOfDay(now);
        }
        break;
      default:
        startDate = subDays(startOfDay(now), 6);
        endDate = endOfDay(now);
    }

    return {
      from: formatDateForAPI(startDate),
      to: formatDateForAPI(endDate),
    };
  };

  useEffect(() => {
    const loadData = async () => {
      const data = await fetchCurrentUser();
      if (data) {
        setUserType(data.type);
        setTenantCode(data.tenant_code);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    if (tenantCode && isTenantRole) {
      fetchOrgs();
    }
  }, [tenantCode]);

  useEffect(() => {
    if (!isInitialLoad) {
      GET_VERIFICATION_APPROVALS_LIST(limit, offset);
    }
  }, [org_code]);

  const fetchOrgs = async (loadMore = false) => {
    try {
      setOrgLoading(true);
      const offset = loadMore ? orgOffset : 0;
      const data = await fetchOrganisationsAPI(tenantCode, offset);

      if (!loadMore && data.options.length <= 1) {
        setHasOrganisations(false);
        setOrganisationOptions([{ name: "All Organisations", value: "" }]);
      } else {
        setHasOrganisations(true);
        if (loadMore) {
          const newOptions = data.options.filter((opt) => opt.value !== "");
          setOrganisationOptions((prev) => [...prev, ...newOptions]);
        } else {
          setOrganisationOptions(data.options);
        }
      }

      setOrgNext(data.next);
      setOrgOffset(
        offset + data.options.filter((opt) => opt.value !== "").length
      );
    } catch (error) {
      console.error("Error fetching organisations:", error);
      setHasOrganisations(false);
      Toast({
        type: "error",
        title: "Error",
        message: "Failed to fetch organisations",
      });
    } finally {
      setOrgLoading(false);
    }
  };

  const handleOrgScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (
      scrollHeight - scrollTop <= clientHeight + 1 &&
      orgNext &&
      !orgLoading
    ) {
      fetchOrgs(true);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        orgDropdownRef.current &&
        !orgDropdownRef.current.contains(event.target)
      ) {
        setOrgDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!orgDropdownOpen) {
      setOrgOffset(0);
    }
  }, [orgDropdownOpen]);

  const amountStatusOptions = [
    { name: "Payment Status", value: null },
    { name: "Paid", value: "Paid" },
    { name: "Free", value: "Free" },
  ];

  const approvalStatusOptions = [
    { name: "Approval Status", value: null },
    { name: "Approved", value: "Approved" },
    { name: "Rejected", value: "Rejected" },
    { name: "Pending", value: "Approval Pending" },
  ];

  const documentTypeOptions = [
    { name: "Document Type", value: null },
    { name: "VC", value: "VC" },
    { name: "Legacy", value: "Legacy" },
    { name: "Data", value: "Data" },
  ];

  const updateAmountStatus = useCallback(
    (value) => {
      if (!isInitialLoad) {
        setSelectedAmountStatus(value);
        setOffset(0);
      }
    },
    [isInitialLoad]
  );

  const updateDocumentType = useCallback(
    (value) => {
      if (!isInitialLoad) {
        setSelectedDocumentType(value);
        setOffset(0);
      }
    },
    [isInitialLoad]
  );

  const updateApprovalStatus = useCallback(
    (value) => {
      if (!isInitialLoad) {
        setSelectedApprovalStatus(value);
        setOffset(0);
      }
    },
    [isInitialLoad]
  );

  const updateVerifierOrganization = useCallback(
    (value) => {
      if (!isInitialLoad) {
        setSelectedVerifierOrganization(value);
        setOffset(0);
      }
    },
    [isInitialLoad]
  );

  const handleRemoveTransaction = (transaction_id) => {
    setRespContentList((prevList) =>
      prevList.filter(
        (transaction) => transaction.transaction_id !== transaction_id
      )
    );
  };

  const handleCheckboxChange = (transactionId, isChecked) => {
    setSelectedTransactions((prevSelected) => {
      let newSelected;

      if (isChecked) {
        const transaction = respContentList.find(
          (t) => t.transaction_id === transactionId
        );
        newSelected = [...prevSelected, transaction];
      } else {
        newSelected = prevSelected.filter(
          (t) => t.transaction_id !== transactionId
        );
      }

      const currentPageIds = respContentList.map((t) => t.transaction_id);
      const selectedOnCurrentPage = newSelected.filter((t) =>
        currentPageIds.includes(t.transaction_id)
      );

      const allCurrentPageSelected =
        selectedOnCurrentPage.length === respContentList.length;
      setAllSelected(allCurrentPageSelected);

      return newSelected;
    });
  };

  const updateSelectedTransactions = useCallback(
    (updatedTransactions) => {
      setSelectedTransactions(updatedTransactions);

      if (respContentList.length > 0) {
        const currentPageIds = respContentList.map((t) => t.transaction_id);
        const selectedOnCurrentPage = updatedTransactions.filter((t) =>
          currentPageIds.includes(t.transaction_id)
        );

        const allCurrentPageSelected =
          selectedOnCurrentPage.length === respContentList.length;
        setAllSelected(allCurrentPageSelected);
      } else {
        setAllSelected(false);
      }
    },
    [respContentList]
  );

  const handleHeaderCheckboxChange = () => {
    if (allSelected) {
      setSelectedTransactions((prevSelected) =>
        prevSelected.filter(
          (t) =>
            !respContentList.some(
              (item) => item.transaction_id === t.transaction_id
            )
        )
      );
    } else {
      setSelectedTransactions((prevSelected) => {
        const newSelections = respContentList.filter(
          (item) =>
            !prevSelected.some((t) => t.transaction_id === item.transaction_id)
        );
        return [...prevSelected, ...newSelections];
      });
    }
    setAllSelected(!allSelected);
  };

  const handleBulkReject = () => {
    if (selectedTransactions.length > 0) {
      setActionType("Rejected");
      setIsModalOpen(true);
    }
  };

  const handleBulkAccept = () => {
    if (selectedTransactions.length > 0) {
      setActionType("Approved");
      setIsModalOpen(true);
    }
  };

  const handleConfirmAction = async (
    actionType,
    transaction_id,
    comment = ""
  ) => {
    if (selectedTransactions.length > 0 || transaction_id !== undefined) {
      const body = {
        transaction_id:
          selectedTransactions.length > 0
            ? selectedTransactions.map(
                (transaction) => transaction.transaction_id
              )
            : transaction_id,
        verification_approval_status: actionType,
        ...(comment?.trim() && { issuer_comments: comment.trim() }),
      };
      try {
        const response = await POST(
          EntityConstants.GET_VERIFICATION_APPROVALS_LIST,
          body
        );

        if (response.ok) {
          Toast({
            type: "success",
            title: "Transaction Updated",
            message: `Transaction has been ${actionType.toLowerCase()} successfully!`,
          });

          setSelectedTransactions([]);
          setAllSelected(false);
          setIssuerComments("");

          GET_VERIFICATION_APPROVALS_LIST(limit, offset);
        } else {
          const errorData = await response.json();
          Toast({
            type: "error",
            title: "Error",
            message:
              errorData.message ||
              "An error occurred while updating the transaction.",
          });
        }
      } catch (error) {
        console.error("Error during the POST request:", error);
        Toast({
          type: "error",
          title: "Error",
          message: "An error occurred while updating the transaction status.",
        });
      }
    }

    setIsModalOpen(false);
  };

  const handleCardClick = (transaction) => {
    const currentFilters = {
      search,
      offset,
      amountStatus: selectedAmountStatus,
      approvalStatus: selectedApprovalStatus,
      verifierOrg: selectedVerifierOrganization,
      documentType: selectedDocumentType,
      org_code,
    };
    const status =
      selectedApprovalStatus ||
      transaction.verification_approval_status ||
      "Approval Pending";

    navigate(`/issuer/approvals/${transaction.transaction_id}`, {
      state: {
        ...transaction,
        filters: currentFilters,
        status,
      },
    });
  };

  const fetchVerifierOrganizations = async () => {
    try {
      const url = org_code
        ? `${EntityConstants.GET_VERIFIER_ORGANIZATIONS_LIST}?org_code=${org_code}`
        : EntityConstants.GET_VERIFIER_ORGANIZATIONS_LIST;

      const response = await GET(url);

      const data = await response.json();

      if (data?.data) {
        setVerifierOrganizations([
          { name: "Verifier Organization", value: null },
          ...data.data.map((org) => ({
            name: org.org_name,
            value: org.org_code,
          })),
        ]);
      }
    } catch (error) {
      console.error("Error fetching Verifier Organizations:", error);
    }
  };

  useEffect(() => {
    if (!isInitialLoad) {
      fetchVerifierOrganizations();
    }
  }, [org_code]);

  const handleExport = async () => {
    if (isExporting) return;

    setIsExporting(true);

    const dateRangeParams = getDateRangeForAPI();

    try {
      const payload = {
        ...(dateRangeParams.all_transactions && { all_transactions: true }),
        ...(dateRangeParams.from && { from: dateRangeParams.from }),
        ...(dateRangeParams.to && { to: dateRangeParams.to }),
        ...(org_code && { org_code }),

        ...(selectedVerifierOrganization && {
          verifier_organization: selectedVerifierOrganization,
        }),
        ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
        ...(selectedAmountStatus && {
          payment_status: selectedAmountStatus.toLowerCase(),
        }),
        ...(selectedApprovalStatus
          ? {
              verification_approval_status:
                APPROVAL_STATUS_MAP[selectedApprovalStatus],
            }
          : {
              verification_approval_status:
                APPROVAL_STATUS_MAP["Approval Pending"],
            }),

        ...(selectedDocumentType && {
          document_type: selectedDocumentType,
        }),
      };

      const Response = await POST(
        EntityConstants.VERIFICATION_APPROVALS_EXPORT_EXCEL_CREATE,
        payload
      );

      if (!Response.ok) {
        throw new Error("Failed to start export");
      }

      const startData = await Response.json();

      const { export_id } = startData;

      if (!export_id) {
        throw new Error("Export ID not received");
      }

      let pollCount = 0;
      const MAX_POLLS = 20;
      const pollStatus = async () => {
        try {
          const statusResponse = await GET(
            `${EntityConstants.VERIFICATION_APPROVALS_EXPORT_EXCEL}/${export_id}/status/`
          );
          if (!statusResponse.ok) {
            throw new Error("Failed to fetch export status");
          }
          const statusData = await statusResponse.json();
          pollCount += 1;
          if (pollCount >= MAX_POLLS) {
            clearInterval(exportIntervalRef.current);
            exportIntervalRef.current = null;
            setIsExporting(false);
            Toast({
              type: "error",
              title: "Export timeout",
              message: "Export took too long. Please try again.",
            });

            return;
          }

          if (statusData.status === "COMPLETED") {
            clearInterval(exportIntervalRef.current);
            exportIntervalRef.current = null;
            setIsExporting(false);
            if (statusData.file_url) {
              downloadFromUrl(statusData.file_url, `data_${export_id}.xlsx`);
              Toast({
                type: "success",
                title: "Export Ready",
                message: "Excel file is ready for download",
              });
            }
          }
        } catch (error) {
          clearInterval(exportIntervalRef.current);
          exportIntervalRef.current = null;
          setIsExporting(false);

          Toast({
            type: "error",
            title: "Export failed",
            message: "Unable to fetch export status",
          });
        }
      };

      exportIntervalRef.current = setInterval(pollStatus, EXPORT_POLL_INTERVAL);
    } catch (error) {
      setIsExporting(false);
      Toast({
        type: "error",
        title: "Export failed",
        message: error.message || "Unable to export data",
      });
    }
  };

  const GET_VERIFICATION_APPROVALS_LIST = (limit, currentOffset) => {
    setIsSearching(true);
    const dateRangeParams = getDateRangeForAPI();
    let apiUrl = `${EntityConstants.GET_VERIFICATION_APPROVALS_LIST}?limit=${limit}&offset=${currentOffset}`;

    if (dateRangeParams.all_transactions) {
      apiUrl += `&all_transactions=true`;
    } else {
      apiUrl += `&from=${encodeURIComponent(
        dateRangeParams.from
      )}&to=${encodeURIComponent(dateRangeParams.to)}`;
    }

    if (selectedVerifierOrganization) {
      apiUrl += `&verifier_organization=${selectedVerifierOrganization}`;
    }
    if (selectedAmountStatus) {
      apiUrl += `&payment_status=${selectedAmountStatus}`;
    }
    if (selectedDocumentType) {
      apiUrl += `&document_type=${selectedDocumentType}`;
    }
    if (selectedApprovalStatus) {
      apiUrl += `&verification_approval_status=${selectedApprovalStatus}`;
    }
    if (debouncedSearchTerm) {
      apiUrl += `&search=${debouncedSearchTerm}`;
    }
    if (org_code) {
      apiUrl += `&org_code=${org_code}`;
    }
    const temp_array = respContentList;
    setRespContentList([]);

    GET(apiUrl)
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          Toast({
            type: "error",
            title: "Error",
            message: data.error,
          });
          setRespContentList(temp_array);
        } else {
          setRespContentList(data.results);
          setPrevious(data.previous);
          setNext(data.next);

          const currentPageIds = data.results.map((t) => t.transaction_id);
          const filteredSelections = selectedTransactions.filter((t) =>
            currentPageIds.includes(t.transaction_id)
          );

          setSelectedTransactions(filteredSelections);

          if (data.results.length > 0) {
            const allCurrentPageSelected =
              filteredSelections.length === data.results.length;
            setAllSelected(allCurrentPageSelected);
          } else {
            setAllSelected(false);
          }
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setIsSearching(false);
        setIsSearched(true);
      });
  };

  const DateFilterDropdown = () => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const [tempStartDate, setTempStartDate] = useState(customStartDate);
    const [tempEndDate, setTempEndDate] = useState(customEndDate);

    const today = useMemo(() => new Date(), []);
    const minStartDate = useMemo(() => {
      const minDate = new Date(today);
      minDate.setDate(minDate.getDate() - 180);
      return minDate;
    }, [today]);
    const maxEndDate = useMemo(() => {
      if (!tempStartDate) return today;
      const maxFromStartDate = new Date(tempStartDate);
      maxFromStartDate.setDate(maxFromStartDate.getDate() + 180);
      return maxFromStartDate < today ? maxFromStartDate : today;
    }, [tempStartDate, today]);
    const minEndDate = useMemo(() => {
      return tempStartDate || minStartDate;
    }, [tempStartDate, minStartDate]);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target)
        ) {
          setIsOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const presetOptions = [
      { label: "Select Date ", value: "dateselection" },
      { label: "Recent", value: "recent" },
      { label: "Last 30 Days", value: "last30days" },
      { label: "Last 3 Months", value: "last3months" },
      { label: "Last 6 Months", value: "last6months" },
      { label: "All", value: "all" },
    ];

    const handlePresetSelect = (preset) => {
      setDateRange(preset);
      setCustomStartDate(null);
      setCustomEndDate(null);
      setIsOpen(false);
    };

    const handleApplyCustomDate = () => {
      if (tempStartDate && tempEndDate && tempEndDate >= tempStartDate) {
        setCustomStartDate(tempStartDate);
        setCustomEndDate(tempEndDate);
        setDateRange("custom");
        setIsOpen(false);
      }
    };

    const getDisplayText = () => {
      if (dateRange === "custom" && customStartDate && customEndDate) {
        return `${format(customStartDate, "MMM d, yyyy")} - ${format(
          customEndDate,
          "MMM d, yyyy"
        )}`;
      }
      return (
        presetOptions.find((opt) => opt.value === dateRange)?.label || "Recent"
      );
    };

    return (
      <div className="relative inline-block text-left" ref={dropdownRef}>
        <button
          type="button"
          className="flex justify-between items-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
          onClick={() => setIsOpen(!isOpen)}
        >
          {getDisplayText()}
          <svg
            className="-mr-1 ml-2 h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>

        {isOpen && (
          <div className="origin-top-left absolute left-0 mt-3 w-[23rem] rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
            <div className="" role="menu" aria-orientation="vertical">
              {presetOptions.map((option) => (
                <button
                  key={option.value}
                  className={`${
                    dateRange === option.value
                      ? "bg-gray-100 text-gray-900"
                      : "text-gray-700"
                  } block w-full text-left px-4 py-2 text-sm hover:bg-gray-100`}
                  onClick={() => handlePresetSelect(option.value)}
                >
                  {option.label}
                </button>
              ))}

              <hr className=" border-gray-200" />

              <div className="px-4 py-2 text-sm text-gray-500 font-medium">
                Custom period:
              </div>

              <div className="px-4 py-2 space-y-2">
                <div className="flex items-center space-x-3">
                  <div className="relative w-[10rem]">
                    <DatePicker
                      selected={tempStartDate}
                      onChange={(date) => {
                        setTempStartDate(date);

                        if (!date) {
                          setTempEndDate(null);
                          return;
                        }

                        if (tempEndDate && tempEndDate < date) {
                          setTempEndDate(null);
                          return;
                        }

                        const maxFromStart = new Date(date);
                        maxFromStart.setDate(maxFromStart.getDate() + 180);
                        const upperBound =
                          maxFromStart < today ? maxFromStart : today;

                        if (tempEndDate && tempEndDate > upperBound) {
                          setTempEndDate(null);
                        }
                      }}
                      selectsStart
                      startDate={tempStartDate}
                      endDate={tempEndDate}
                      dateFormat={"dd/MM/yyyy"}
                      className="w-full border border-gray-300 rounded pl-3 pr-12 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                      placeholderText="Start date"
                      minDate={minStartDate}
                      maxDate={today}
                      showYearDropdown
                      showMonthDropdown
                      dropdownMode="select"
                      yearDropdownItemNumber={15}
                      scrollableYearDropdown
                    />
                    <CalendarDays className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500 pointer-events-none" />
                  </div>

                  <div className="relative w-[10rem]">
                    <DatePicker
                      selected={tempEndDate}
                      onChange={(date) => setTempEndDate(date)}
                      selectsEnd
                      startDate={tempStartDate}
                      endDate={tempEndDate}
                      dateFormat={"dd/MM/yyyy"}
                      minDate={minEndDate}
                      maxDate={maxEndDate}
                      className="w-full border border-gray-300 rounded pl-12 pr-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                      placeholderText="End date"
                      disabled={!tempStartDate}
                      showYearDropdown
                      showMonthDropdown
                      dropdownMode="select"
                      yearDropdownItemNumber={15}
                      scrollableYearDropdown
                    />
                    <CalendarDays className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500 pointer-events-none" />
                  </div>
                </div>
                {!tempStartDate ? (
                  <div className="text-xs text-gray-500">
                    Select a start date between{" "}
                    {format(minStartDate, "MMM d, yyyy")} and{" "}
                    {format(today, "MMM d, yyyy")}
                  </div>
                ) : (
                  <div className="text-xs text-gray-500">
                    End date must be between{" "}
                    {format(tempStartDate, "MMM d, yyyy")} and{" "}
                    {format(today, "MMM d, yyyy")}
                  </div>
                )}

                <button
                  onClick={handleApplyCustomDate}
                  disabled={
                    !tempStartDate ||
                    !tempEndDate ||
                    tempEndDate < tempStartDate
                  }
                  className="w-full bg-[#3538CD] text-white font-medium py-2 px-4 rounded-md text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed mt-3"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const refreshTable = useCallback(() => {
    GET_VERIFICATION_APPROVALS_LIST(limit, offset);
  }, [limit, offset, GET_VERIFICATION_APPROVALS_LIST]);

  useEffect(() => {
    if (savedFilters.approvalStatus) {
      setSelectedApprovalStatus(savedFilters.approvalStatus);
    }
    if (savedFilters.documentType) {
      setSelectedDocumentType(savedFilters.documentType);
    }
    if (savedFilters.offset) {
      const savedOffset = parseInt(savedFilters.offset);
      setOffset(savedOffset);
    }
    if (savedFilters.org_code) {
      setOrgCode(savedFilters.org_code);
      const foundOrg = organisationOptions.find(
        (opt) => opt.value === savedFilters.org_code
      );
      if (foundOrg) {
        setSelectedOrganisation(savedFilters.org_code);
      }
    }
    fetchVerifierOrganizations();
  }, [location.state]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(search);
    }, SEARCH_DEBOUNCE_DELAY);
    return () => {
      clearTimeout(handler);
      if (exportIntervalRef.current) {
        clearInterval(exportIntervalRef.current);
      }
    };
  }, [search]);

  useEffect(() => {
    GET_VERIFICATION_APPROVALS_LIST(limit, offset);

    if (isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [
    debouncedSearchTerm,
    selectedAmountStatus,
    selectedApprovalStatus,
    selectedVerifierOrganization,
    selectedDocumentType,
    limit,
    offset,
    dateRange,
    customStartDate,
    customEndDate,
  ]);

  const handlePrevious = () => {
    setOffset(offset - limit);
  };

  const handleNext = () => {
    setOffset(offset + limit);
  };

  return (
    <div className="flex flex-col items-center w-full mt-20 bg-cfSecondary min-h-screen">
      <div
        className={`w-full bg-cfSearchContainer flex justify-center py-[0.8rem] px-4`}
      >
        <div
          className={`flex flex-col sm:flex-row items-start sm:items-center justify-between w-full`}
        >
          <p className="text-cfHeadingText font-semibold self-start my-auto">
            Verification Approvals
          </p>
          <form
            onSubmit={(e) => e.preventDefault()}
            className=" bg-cfSecondary p-2 rounded-lg w-full sm:w-[25rem] self-center ml-[0rem] md:ml-[0.630rem] md:self-end md:m-0"
          >
            <input
              type="text"
              name="search"
              id="search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value.toLowerCase());
                setOffset(0);
              }}
              placeholder="Search for organization name"
              className="outline-none border-none p-0 placeholder:text-sm w-full"
              style={{ fontSize: "0.875rem", lineHeight: "1.25rem" }}
            />
          </form>
        </div>
      </div>

      <div className="flex md:flex-wrap items-center gap-4 justify-start mx-[1rem] mt-[0.6rem] mb-[-0.6rem] w-full">
        <div className="flex flex-col md:flex-row w-full items-start md:items-center md:justify-between gap-4 px-4 md:px-8">
          <div className="flex flex-col md:flex-row gap-4 w-full md:w-auto md:items-center">
            <div className="flex md:items-center">
              <DateFilterDropdown />
            </div>

            <div className="flex flex-col md:flex-row gap-4 w-full md:w-auto md:flex-nowrap md:overflow-visible">
              <div className="w-full md:w-[260px]">
                <Dropdown
                  options={verifierOrganizations}
                  selected={
                    verifierOrganizations.find(
                      (option) => option.value === selectedVerifierOrganization
                    ) || verifierOrganizations[0]
                  }
                  onSelect={updateVerifierOrganization}
                  buttonClass="px-4 py-2 border rounded-md w-full"
                  menuClass="z-20 w-full"
                />
              </div>

              <div className="w-full md:w-[200px]">
                <Dropdown
                  options={amountStatusOptions}
                  selected={
                    amountStatusOptions.find(
                      (option) => option.value === selectedAmountStatus
                    ) || amountStatusOptions[0]
                  }
                  onSelect={updateAmountStatus}
                  buttonClass="px-4 py-2 border rounded-md w-full"
                  menuClass="z-20 w-full"
                />
              </div>

              <div className="w-full md:w-[200px]">
                <Dropdown
                  options={approvalStatusOptions}
                  selected={
                    approvalStatusOptions.find(
                      (option) => option.value === selectedApprovalStatus
                    ) || approvalStatusOptions[0]
                  }
                  onSelect={updateApprovalStatus}
                  buttonClass="px-4 py-2 border rounded-md w-full"
                  menuClass="z-20 w-full"
                />
              </div>

              <div className="w-full md:w-[200px]">
                <Dropdown
                  options={documentTypeOptions}
                  selected={
                    documentTypeOptions.find(
                      (option) => option.value === selectedDocumentType
                    ) || documentTypeOptions[0]
                  }
                  onSelect={updateDocumentType}
                  buttonClass="px-4 py-2 border rounded-md w-full"
                  menuClass="z-20 w-full"
                />
              </div>

              {isTenantRole && hasOrganisations && (
                <div
                  className="relative w-full md:w-[200px]"
                  ref={orgDropdownRef}
                >
                  <div
                    className="flex justify-between items-center border-[1px] rounded-md p-[0.52rem] text-sm w-full cursor-pointer bg-white"
                    onClick={() => {
                      setOrgDropdownOpen(!orgDropdownOpen);
                      if (!orgDropdownOpen) {
                        fetchOrgs();
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        setOrgDropdownOpen(!orgDropdownOpen);
                        if (!orgDropdownOpen) {
                          fetchOrgs();
                        }
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <span className="truncate">
                      {selectedOrganisation
                        ? organisationOptions.find(
                            (opt) => opt.value === selectedOrganisation
                          )?.name
                        : "All Organisations"}
                    </span>
                    <svg
                      className={`w-5 h-5 ml-2 transition-transform duration-200 ${
                        orgDropdownOpen ? "transform rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                  {orgDropdownOpen && (
                    <div
                      className="absolute z-50 mt-1 w-full max-h-60 overflow-auto border border-gray-300 bg-white rounded-md shadow-lg"
                      onScroll={handleOrgScroll}
                    >
                      {organisationOptions.length > 0 ? (
                        organisationOptions.map((option) => (
                          <div
                            key={option.value}
                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer truncate"
                            onClick={() => {
                              setSelectedOrganisation(option.value);
                              setOrgCode(option.value);
                              setOrgDropdownOpen(false);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                setSelectedOrganisation(option.value);
                                setOrgCode(option.value);
                                setOrgDropdownOpen(false);
                              }
                            }}
                            role="button"
                            tabIndex={0}
                          >
                            {option.name}
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-2 text-gray-500">
                          {orgLoading ? "Loading..." : "No organisations found"}
                        </div>
                      )}
                      {orgLoading && (
                        <div className="px-4 py-2 flex justify-center">
                          Loading...
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto justify-end md:justify-start md:items-center md:shrink-0">
            {/* Download Data Button – always visible */}
            <div className="flex w-full md:w-auto">
              <button
                onClick={handleExport}
                disabled={isExporting || respContentList.length === 0}
                className={`flex items-center justify-center gap-2 px-6 py-2 rounded-md text-white font-medium w-full md:w-auto ${
                  isExporting || respContentList.length === 0
                    ? "bg-indigo-300 cursor-not-allowed"
                    : "bg-[#3538CD] hover:bg-indigo-700"
                }`}
              >
                {isExporting ? (
                  <SmallCFLoader text="Processing..." />
                ) : (
                  "Download Data"
                )}
              </button>
            </div>

            {/* Bulk Approve / Reject Buttons */}
            {selectedTransactions.length > 0 &&
              selectedApprovalStatus !== "Approved" &&
              selectedApprovalStatus !== "Rejected" && (
                <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto md:flex-nowrap">
                  <button
                    className="flex rounded-[8px] items-center justify-center gap-2 w-full px-4 py-2 bg-[#3538CD] text-white font-semibold text-md hover:bg-cfPrimaryDark transition duration-200"
                    onClick={handleBulkAccept}
                  >
                    Approve
                  </button>

                  <button
                    className="flex rounded-[8px] items-center justify-center gap-2 w-full px-4 py-2 bg-white text-black font-semibold text-md border border-gray-800 hover:bg-gray-100 transition duration-200"
                    onClick={handleBulkReject}
                  >
                    Reject
                  </button>
                </div>
              )}
          </div>
        </div>

        <BulkVerificationApprovalModal
          isModalOpen={isModalOpen}
          toggleModal={setIsModalOpen}
          actionType={actionType}
          handleConfirmAction={() =>
            handleConfirmAction(actionType, undefined, issuerComments)
          }
          selectedTransactions={selectedTransactions}
          issuerComments={issuerComments}
          setIssuerComments={setIssuerComments}
          refreshTable={refreshTable}
          updateSelectedTransactions={updateSelectedTransactions}
        />
      </div>

      <div className="overflow-x-auto w-full px-4 md:px-8 mt-[-0.2rem]">
        <div className="flex flex-col justify-center gap-2 min-w-[73.5rem] md:w-full">
          <div className="flex bg-[#E0EAFF] items-center min-h-[5rem] border py-4 px-6 mt-6 ml-[0.05rem] mr-[0.05rem]">
            <div className="flex w-[2.0%] justify-center items-center">
              {selectedApprovalStatus !== "Approved" &&
                selectedApprovalStatus !== "Rejected" && (
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={handleHeaderCheckboxChange}
                    className="cursor-pointer rounded-lg"
                  />
                )}
            </div>
            <div className="flex w-[14.29%] justify-center items-center text-cfTableHeaderText font-bold text-[14px]">
              Verifier Name
            </div>
            <div className="flex w-[14.29%] justify-center text-cfTableHeaderText font-bold text-[14px]">
              Verifier Organization
            </div>
            <div className="flex w-[14.29%] justify-center text-cfTableHeaderText font-bold text-[14px]">
              Created At
            </div>
            <div className="flex w-[14.29%] justify-center text-cfTableHeaderText font-bold text-[14px]">
              CredIssuer Verified
            </div>
            <div className="flex w-[14.29%] justify-center text-cfTableHeaderText font-bold text-[14px]">
              Amount Paid
            </div>
            <div className="flex w-[14.29%] justify-center text-cfTableHeaderText font-bold text-[14px]">
              Document Type
            </div>
            <div className="flex w-[14.29%] justify-center text-cfTableHeaderText font-bold text-[14px]">
              Actions
            </div>
          </div>

          {isSearching && <CfLoader />}
          {!isSearching &&
            isSearched &&
            respContentList.length > 0 &&
            respContentList.map((transaction) => (
              <VerificationApprovalsLongCard
                key={transaction.transaction_id}
                {...transaction}
                onCheckboxChange={handleCheckboxChange}
                isChecked={selectedTransactions.some(
                  (t) => t.transaction_id === transaction.transaction_id
                )}
                handleConfirmAction={handleConfirmAction}
                selectedTransactions={selectedTransactions}
                onRemoveTransaction={handleRemoveTransaction}
                onClick={() => handleCardClick(transaction)}
                selectedApprovalStatus={selectedApprovalStatus}
                setIssuerComments={setIssuerComments}
              />
            ))}
          {!isSearching && isSearched && respContentList.length === 0 && (
            <p className="text-center p-4 font-semibold">No results found</p>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between mt-6 px-8 w-full">
        <button
          onClick={handlePrevious}
          className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
          disabled={previous === null}
        >
          <span className="flex items-center justify-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="w-5 h-5 rtl:-scale-x-100"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
              />
            </svg>

            <span>previous</span>
          </span>
        </button>

        <button
          onClick={handleNext}
          className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
          disabled={next === null}
        >
          <span className="flex items-center justify-center gap-2">
            <span>Next</span>

            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="w-5 h-5 rtl:-scale-x-100"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
              />
            </svg>
          </span>
        </button>
      </div>
    </div>
  );
};

export default VerificationApprovals;
